import { format, parseISO } from 'date-fns'
import type { AssignmentStatus } from '@/db/schema/enums'
import {
  cardHours,
  type ScheduleDay,
  type TrayCard,
  type Week,
} from './auto-place'
import { hoursToMinutes } from './duration'

/**
 * One day the details modal can offer as an assignment target (LG-360 §4).
 *
 * `disabledReason` is non-null for a day the server would refuse — the modal
 * still renders it, greyed, carrying the reason, because "Thursday is missing"
 * and "Thursday is in a published week" are different things for a coach to
 * discover.
 */
export interface MoveTargetDay {
  weekIdx: number
  dayIdx: number
  /** `${weekIdx}:${dayIdx}` — the select's option value. */
  value: string
  label: string
  /** The day the card sits on right now; absent for a tray card. */
  current: boolean
  disabledReason: string | null
  /**
   * The card would not fit in the day's budget. Advisory only, never a reason
   * to disable: neither `place` nor `move` gates on budget
   * (`planning-service.ts` — "anything placeable is movable"), so refusing here
   * would block a move the coach is entitled to make and the server would
   * happily accept.
   */
  overBudget: boolean
}

export interface MoveTargetWeek {
  weekId: string
  label: string
  days: MoveTargetDay[]
}

const DAY_KIND_REASON: Record<ScheduleDay['kind'], string | null> = {
  normal: null,
  blocked: 'blocked day',
  interview: 'interview day',
  avail: 'availability only',
}

/**
 * Why a status blocks re-dating, or `null` when it does not — a
 * `Record<AssignmentStatus, …>`, not a two-armed ternary or an ad hoc
 * allow-list (LG-360 expert review, B4). TypeScript requires every member of
 * the closed `assignmentStatusEnum` lifecycle to have an entry, so a new or
 * renamed status becomes a compile error here rather than silently falling
 * through to whichever sentence a ternary's `else` branch happened to say.
 *
 * Mirrors the service's own gate: `move` requires a currently-placed
 * assignment whose status is `planned` or `in_progress`, and `place`
 * requires the same of an unplaced one — a `submitted` assignment is work
 * the client has already handed in, and the service does not accept it into
 * either write, so it stays blocked here too rather than offering a picker
 * the server would then refuse. What changed is only the sentence: it used
 * to fall into the `completed` branch and tell the coach to "mark it not
 * done first" about a card `CompletionControl` offers no such toggle for.
 */
const REDATE_BLOCKED_REASON: Record<AssignmentStatus, string | null> = {
  planned: null,
  in_progress: null,
  submitted:
    'This card was submitted for review and cannot be rescheduled until it has been reviewed.',
  completed: 'A completed card cannot be rescheduled — mark it not done first.',
  cancelled: 'A cancelled card cannot be rescheduled.',
}

/**
 * Whether a card is in a state that can be re-dated at all.
 *
 * Undefined status is the mock board's, which has no lifecycle to violate.
 */
export function canRedateCard(card: TrayCard): boolean {
  if (card.fixed === true || card.done === true) return false
  return (
    card.status === undefined || REDATE_BLOCKED_REASON[card.status] === null
  )
}

/**
 * The reason `AssignedDayPicker` shows when `canRedateCard` is false, or
 * `null` for the `fixed`/`done` cases that block re-dating without a status
 * of their own to blame — the caller supplies its own generic fallback for
 * those, since there is no per-status truth to report.
 */
export function redateBlockedReason(card: TrayCard): string | null {
  return card.status === undefined ? null : REDATE_BLOCKED_REASON[card.status]
}

const usedMinutes = (day: ScheduleDay, excludeCardId: string): number =>
  day.cards
    .filter((card) => card.id !== excludeCardId)
    .reduce((total, card) => total + hoursToMinutes(cardHours(card)), 0)

/**
 * Every day on the board, grouped by week, annotated with whether this card may
 * be assigned to it.
 *
 * The legality rules are the server's, restated here so the coach learns the
 * answer before spending a round trip on it:
 *   - the week must be `draft` — `requireDraftWeek` refuses `published` and the
 *     elapsed weeks the client models as `past`;
 *   - the day must be `kind: 'normal'`;
 *   - and, stricter than the server, the date must not be in the past. The
 *     service's `assertWeekNotPast` is week-granular, so it would in fact allow
 *     backdating a card to Monday on a Wednesday — LG-360 §4 asks for "today or
 *     future dates only", which is this rule, and it lives here because nothing
 *     on the server enforces it.
 *
 * `todayKey` is passed in rather than read from the clock so the rule is
 * testable and so the caller decides which day "today" is.
 */
export function buildMoveTargets(
  weeks: readonly Week[],
  card: TrayCard,
  todayKey: string,
): MoveTargetWeek[] {
  return weeks.map((week, weekIdx) => {
    const weekReason =
      week.status === 'past'
        ? 'week already elapsed'
        : week.status === 'published'
          ? 'published week — unpublish to edit it'
          : null

    return {
      weekId: week.id,
      // Weeks label as `Week 3 · Jul 27` on the board; the picker only needs
      // the leading name, since every option under it carries its own date.
      label: week.label.split(' · ')[0] ?? week.label,
      days: week.days.map((day, dayIdx) => {
        // A day the mock board synthesised has no date. Its label is all there
        // is to show, and the past-date rule has nothing to compare, so it is
        // left to the week and kind rules alone rather than guessed at.
        const label = day.dateKey
          ? format(parseISO(day.dateKey), 'EEE, MMM d')
          : day.label
        const pastReason =
          day.dateKey && day.dateKey < todayKey ? 'in the past' : null

        return {
          weekIdx,
          dayIdx,
          value: `${weekIdx}:${dayIdx}`,
          label,
          current: day.cards.some((candidate) => candidate.id === card.id),
          // Week first: it is the coarsest true statement about the day, and
          // "published week" explains a whole row of greyed days at once where
          // "in the past" would explain only some of them.
          disabledReason: weekReason ?? DAY_KIND_REASON[day.kind] ?? pastReason,
          overBudget:
            usedMinutes(day, card.id) + hoursToMinutes(cardHours(card)) >
            hoursToMinutes(day.budget ?? 0),
        }
      }),
    }
  })
}
